import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './Articles.css';
import ChevronRight from '../../icons/ChevronRight';
import { useTranslation } from 'react-i18next';
import useLanguagePrefix from '../../LanguagePrefix';

function Article() {
  const { slug, categoryName, subCategoryName } = useParams();
  const [article, setArticle] = useState(null);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const langPrefix = useLanguagePrefix();

  const formattedCategoryName = categoryName?.replace(/-/g, ' ');
  const formattedSubCategoryName = subCategoryName?.replace(/-/g, ' ');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [articleRes, subRes] = await Promise.all([
          fetch(`https://api.prostay.com/WebsiteKnowledgebase/get-article-by-slug/${slug}`),
          fetch(`https://api.prostay.com/WebsiteKnowledgebase/get-articles/${formattedCategoryName}`)
        ]);

        if (!articleRes.ok) throw new Error('Failed to fetch article');
        if (!subRes.ok) throw new Error('Failed to fetch subcategories');

        const articleData = await articleRes.json();
        const subData = await subRes.json();

        setArticle(articleData);
        setSubcategories(subData.subcategories || []);
      } catch (err) {
        console.error(err);
        setArticle(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [slug, formattedCategoryName]);

  const generateSlug = title =>
    title
      .normalize('NFKD')                 // turn “Č” → “Č”, “é” → “é” …
      .replace(/[\u0300-\u036f]/g, '')   // … then drop the diacritics marks
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '')      // throw away punctuation like ":" "'" "&" …
      .replace(/\s+/g, '-')              // spaces → single dash
      .replace(/-+/g, '-')               // collapse multiple dashes
      .replace(/^-|-$/g, '');

  const capitalizeWords = (str) => {
    return str.replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const getSubcategoryLink = (sub, formattedSub) => {
    if (sub.knowledgebaseArticles?.length > 0) {
      const first = sub.knowledgebaseArticles[0];
      const slug = generateSlug(first.title);
      return `/resources/${categoryName}/${formattedSub}/${slug}/`;
    } else {
      return `/resources/${categoryName}/${formattedSub}/`;
    }
  }

  return (
    <div className="article-page">
      <Helmet>
        <title>{article?.metaTitle || article?.title || 'Prostay Article'}</title>
        <meta name="description" content={article?.metaDescription || ''} />
        <link rel="canonical" href={`https://www.prostay.com${langPrefix}/resources/${categoryName}/${subCategoryName}/${slug}/`} />

        <link rel="alternate" href={"https://www.prostay.com/en/" + `${categoryName}/${subCategoryName}/${slug}/`} />
        <link rel="alternate" href={"https://www.prostay.com/es/" + `${categoryName}/${subCategoryName}/${slug}/`} />

        <meta property="og:title" content={article?.metaTitle || article?.title || 'Prostay Article'} />
        <meta property="og:description" content={article?.metaDescription || ''} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://www.prostay.com${langPrefix}/resources/${categoryName}/${subCategoryName}/${slug}/`} />
        <meta property="og:image" content='https://prostay.com/images/ogImage.png' />

        <meta property="og:site_name" content="Prostay" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={article?.metaTitle || article?.title || 'Prostay Article'} />
        <meta name="twitter:description" content={article?.metaDescription || ''} />
        <meta name="twitter:image" content='https://prostay.com/images/ogImage.png' />
        <meta name="twitter:site" content="@prostaypms" />
        <meta name="twitter:creator" content="@prostaypms" />
      </Helmet>

      <div className="background-image">
        <div className="background-image-start">
          <div className='d-flex align-items-center gap-2 gap-md-3 breadcrumbs'>
            <a href={`${langPrefix}/`} style={{ textDecoration: 'none' }}><small>{t('Home')}</small></a>
            <ChevronRight />
            <a href={`${langPrefix}/resources/`} style={{ textDecoration: 'none' }}><small>{t('Resources')}</small></a>
            <ChevronRight />
            <a href={`${langPrefix}/resources/${categoryName}/`} style={{ textDecoration: 'none' }}>
              <small>{capitalizeWords(formattedCategoryName)}</small>
            </a>
            <ChevronRight />
            <a href={`${langPrefix}/resources/${categoryName}/${subCategoryName}/`} style={{ textDecoration: 'none' }}>
              <small>{capitalizeWords(formattedSubCategoryName)}</small>
            </a>
            <ChevronRight />
            <small style={{ color: "#1643E0" }}>{article?.title}</small>
          </div>
          <div className='mt-4' style={{ maxWidth: '600px' }}>
            <h2>{article?.title}</h2>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="d-flex align-items-center justify-content-center mt-5">
          <div className="spinner-container">
            <div className="custom-spinner"></div>
          </div>
        </div>
      ) : !article ? (
        <div className="d-flex justify-content-center mt-5">
          <h2>Article not found</h2>
        </div>
      ) : (
        <div className="d-flex justify-content-center">
          <div className="content-grid page-content mt-2 mt-md-3">
            <div className="col">
              <div className="white-content-card">
                <h5>{t('Popular Topics')}</h5>
                <hr />
                {subcategories.map((sub) => {
                  const formattedSub = sub.subcategoryName.toLowerCase().replace(/\s+/g, '-');
                  return (
                    <a
                      key={sub.subcategoryName}
                      className="subcategory-link"
                      href={getSubcategoryLink(sub, formattedSub)}
                    >
                      {sub.subcategoryName}
                    </a>

                  );
                })}
              </div>

              <div className="contact-card d-flex flex-column">
                <h3 style={{ color: '#FFFFFF', fontSize: '20px' }}>{t('Contact support')}</h3>
                <p className='body-small'>{t('24/7 help from our support team')}</p>
                <a href={`${langPrefix}/contact-us/`} style={{ textDecoration: 'none' }}>
                  <button className="light-blue-filled-btn-small" style={{ backgroundColor: "#22242A", color: "#fff", width: "fit-content" }}>
                    <svg width="16" height="16" viewBox="0 0 20 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1 4L8.89 9.26C9.21866 9.47928 9.6049 9.5963 10 9.5963C10.3951 9.5963 10.7813 9.47928 11.11 9.26L19 4M3 15H17C17.5304 15 18.0391 14.7893 18.4142 14.4142C18.7893 14.0391 19 13.5304 19 13V3C19 2.46957 18.7893 1.96086 18.4142 1.58579C18.0391 1.21071 17.5304 1 17 1H3C2.46957 1 1.96086 1.21071 1.58579 1.58579C1.21071 1.96086 1 2.46957 1 3V13C1 13.5304 1.21071 14.0391 1.58579 14.4142C1.96086 14.7893 2.46957 15 3 15Z" stroke="white" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    {t('Get in Touch')}
                  </button>
                </a>
              </div>
            </div>

            <div className="white-content-card">
              <div className="article-content" dangerouslySetInnerHTML={{ __html: article.content }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Article;
